import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PencilSimple, Check, CircleNotch, VideoCamera } from "@phosphor-icons/react";

/**
 * Admin-only inline manager for the dashboard tutorial video. Accepts a bare
 * YouTube video ID or any common YouTube URL (watch / embed / youtu.be / shorts).
 * Persisted server-side via /api/settings/tutorial-video.
 */
export default function TutorialVideoManager() {
  const [videoId, setVideoId] = useState("");
  const [updatedBy, setUpdatedBy] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/settings/tutorial-video").then((r) => {
      setVideoId(r.data?.video_id || "");
      setUpdatedBy(r.data?.updated_by || "");
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const val = (draft || "").trim();
    if (!val) { toast.error("Paste a YouTube URL or video ID first."); return; }
    setSaving(true);
    try {
      const r = await api.put("/settings/tutorial-video", { value: val });
      setVideoId(r.data.video_id);
      toast.success("Tutorial video updated.");
      setEditing(false); setDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the tutorial video.");
    } finally { setSaving(false); }
  };

  return (
    <section
      data-testid="tutorial-video-manager"
      className="mb-8 rounded-xl border border-white/10 bg-[#0E0E0E] p-4 flex items-center gap-3 flex-wrap"
    >
      <VideoCamera size={18} weight="duotone" className="text-amber-300 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Tutorial video (admin)</p>
        {!editing ? (
          <p className="text-sm text-zinc-300 truncate">
            <span className="font-mono text-white" data-testid="tutorial-video-current">{videoId || "—"}</span>
            {updatedBy && <span className="ml-2 text-[11px] text-zinc-500">· set by {updatedBy}</span>}
          </p>
        ) : (
          <input
            data-testid="tutorial-video-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Paste YouTube URL or video ID (e.g. FBrmuv7QUPk)"
            className="mt-1 w-full h-9 rounded-md bg-black/60 border border-white/10 px-3 text-sm text-white outline-none focus:border-amber-400/60"
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setDraft(""); } }}
            autoFocus
          />
        )}
      </div>
      {!editing ? (
        <button
          data-testid="tutorial-video-edit"
          onClick={() => { setDraft(videoId); setEditing(true); }}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 text-sm text-zinc-300 hover:text-white transition-colors"
        >
          <PencilSimple size={14} weight="bold" /> Change
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            data-testid="tutorial-video-save"
            disabled={saving}
            onClick={save}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-sm font-bold disabled:opacity-60"
          >
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} weight="bold" />} Save
          </button>
          <button
            data-testid="tutorial-video-cancel"
            onClick={() => { setEditing(false); setDraft(""); }}
            className="h-8 px-3 rounded-full text-sm text-zinc-400 hover:text-white"
          >Cancel</button>
        </div>
      )}
    </section>
  );
}
